import React, { FunctionComponent, useEffect } from 'react'
import { Panel } from '@afterpaytouch/core'
import { useAccountProgressionIsExistingUser, useAppRouter, useConsumerAccountStatus, useConsumerCountry, useTerminalErrorAcknowledged } from '../../state'
import { PulseIntroModal, usePulseIntroModal, useShowPulseIntroModalOnMount } from '../PulseIntroModal'
import { ExistingUser } from './ExistingUser'
import { NewUser } from './NewUser'
import { WelcomeModal } from './WelcomeModal'
import { useShowWelcomeModalOnMount, useWelcomeModal } from './WelcomeModal/WelcomeModal.hooks'
import { TerminationNoticeModal, useShowTerminationNoticeModalOnMount, useTerminationNoticeModal } from '../TerminationNoticeModal'
import { Route } from '../../config/router'
import { WelcomeWithInitialLimitModal } from './WelcomeWithInitialLimitModal'
import { useShouldShowHomeModal, useWelcomePanelExperience } from './WelcomePanel.hooks'
import { AccountStatus, Country } from '@afterpay/types'
import { useFlag } from '../../hooks'

export const WelcomePanel: FunctionComponent = () => {
  const existingUser = useAccountProgressionIsExistingUser()
  const { creditLimitErrorCode, hasAffordabilityError, isTerminalAffordabilityError, shouldShowWelcomeWithInitialLimit, shouldSuppressHomeModals } =
    useWelcomePanelExperience(existingUser)
  const router = useAppRouter()
  // Acknowledged declines keep home accessible: it hosts the payment-arrangement schedule payment surface
  const terminalErrorAcknowledged = useTerminalErrorAcknowledged()
  const country = useConsumerCountry()
  const accountStatus = useConsumerAccountStatus()
  const hardshipAccountPaymentsEnabled = useFlag('consumer-portal-hardship-account-payments-enabled')
  const isAcknowledgedAffordabilityDecline =
    terminalErrorAcknowledged &&
    hardshipAccountPaymentsEnabled &&
    country === Country.GB &&
    accountStatus === AccountStatus.PAYMENT_ARRANGEMENT &&
    creditLimitErrorCode === 'affordabilityDecline'

  useEffect(() => {
    if (isTerminalAffordabilityError && creditLimitErrorCode != null && !isAcknowledgedAffordabilityDecline) {
      router.push(`${Route.TERMINAL_ERROR}/${creditLimitErrorCode}`)
    }
  }, [isTerminalAffordabilityError, creditLimitErrorCode, isAcknowledgedAffordabilityDecline, router])

  const [isWelcomeModalOpen, { openModal: openWelcomeModal, closeModal: closeWelcomeModal }] = useWelcomeModal()
  useShowWelcomeModalOnMount(openWelcomeModal, shouldSuppressHomeModals)

  const [isPulseIntroModalOpen, { openModal: openPulseIntroModal, closeModal: closePulseIntroModal }] = usePulseIntroModal()
  useShowPulseIntroModalOnMount(openPulseIntroModal, shouldSuppressHomeModals)

  const [isTerminationNoticeModalOpen, { openModal: openTerminationNoticeModal, closeModal: closeTerminationNoticeModal }] = useTerminationNoticeModal()
  useShowTerminationNoticeModalOnMount(openTerminationNoticeModal, shouldSuppressHomeModals)

  const shouldShowHomeModal = useShouldShowHomeModal(shouldSuppressHomeModals)

  return (
    <>
      <Panel kind='Primary'>
        {(existingUser || isAcknowledgedAffordabilityDecline) && !hasAffordabilityError ? (
          <ExistingUser />
        ) : (
          <NewUser hideOnboardingSteps={hasAffordabilityError && existingUser} />
        )}
      </Panel>
      <WelcomeModal isModalOpen={shouldShowHomeModal(isWelcomeModalOpen)} onClose={closeWelcomeModal} />
      <PulseIntroModal isModalOpen={shouldShowHomeModal(isPulseIntroModalOpen)} closeModal={closePulseIntroModal} />
      <TerminationNoticeModal isModalOpen={shouldShowHomeModal(isTerminationNoticeModalOpen)} closeModal={closeTerminationNoticeModal} />
      <WelcomeWithInitialLimitModal isOpen={shouldShowWelcomeWithInitialLimit} />
    </>
  )
}
